import React, { createContext, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import toLower from 'lodash/toLower';
import keys from 'lodash/keys';
import filter from 'lodash/filter';
import isFunction from 'lodash/isFunction';

import { useAsyncLoadingFn } from '@ps-design-system/lib/hooks/use-async-loading-fn';

import { StripePaymentElementFonts, StripePaymentElementStyles, useStripeInstance } from '../utils';

export const StripeElementsContext = createContext({});

export const StripeElementsProvider = ({ children, amount, currency, country, onElementsMounted, onWalletChange, onSubmit }) => {
  const [elements, setElements] = useState(null);
  const [allWallets, setAllWallets] = useState([]);
  const [availableWallets, setAvailableWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState('card');
  const [isInitialized, setIsInitialized] = useState(false);

  const stripe = useStripeInstance();

  // Using paymentRequest API we can fetch available payment methods for particular user (apple/google pay etc.)
  const fetchWallets = useCallback(async () => {
    if (!stripe) {
      return;
    }
    const paymentRequest = stripe.paymentRequest({
      country,
      currency: toLower(currency),
      total: {
        label: 'Peerspace',
        amount: Math.floor(amount * 100),
      }
    });

    const result = await paymentRequest.canMakePayment();

    if (!result) {
      return;
    }

    const all = keys(result);
    const available = filter(all, (key) => !!result[key] && key !== 'link');

    setAllWallets(all);
    setAvailableWallets(available);
  }, [stripe, country, currency, amount]);

  // Creates elements instance and fetch wallets for the 1st time & updates existing element's amounts
  useEffect(() => {
    if (!amount || !currency || !stripe) {
      setSelectedWallet('card');
      return;
    }

    const options = {
      fonts: StripePaymentElementFonts,
      appearance: StripePaymentElementStyles,
      amount: Math.floor(amount * 100),
      currency: toLower(currency),
      paymentMethodCreation: 'manual',
      mode: 'payment'
    };

    // Create stripe elements only once
    if (!isInitialized || !elements) {
      fetchWallets().then(() => {
        const elements = stripe.elements(options);
        setElements(elements);
        setIsInitialized(true);
        isFunction(onElementsMounted) && onElementsMounted(elements);
      })
    }

    // As amount changes we need to update that amount in stripe elements instance
    elements?.update(options);
}, [amount, currency, stripe, country, onElementsMounted, isInitialized]);

  useEffect(() => {
    if (isFunction(onWalletChange)) {
      onWalletChange(selectedWallet)
    }
  }, [selectedWallet, onWalletChange]);

  const [handleSubmit, isSubmitting] = useAsyncLoadingFn(onSubmit);

  return <StripeElementsContext.Provider
    value={{
      elements,
      allWallets,
      availableWallets,
      selectedWallet,
      setSelectedWallet,
      onSubmit: handleSubmit,
      isSubmitting,
      isInitialized
    }}
  >
    {children}
  </StripeElementsContext.Provider>;
};

StripeElementsProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ])
};
